/**
 * Create Blog Post Dialog Component
 * Dialog for creating a new blog post (discussion) on user's profile
 */

import { useState, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material'
import {
  Close as CloseIcon,
  CameraAlt as CameraIcon,
} from '@mui/icons-material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { socialApi } from '@api/endpoints/social'

interface CreateBlogPostDialogProps {
  open: boolean
  onClose: () => void
  userId: string
}

export function CreateBlogPostDialog({ open, onClose, userId }: CreateBlogPostDialogProps) {
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)

  const createDiscussionMutation = useMutation({
    mutationFn: async () => {
      let imageId: string | undefined = undefined

      // Upload image first if selected
      if (selectedImage) {
        setUploadingImage(true)
        try {
          const response = await socialApi.uploadDiscussionImage(selectedImage)
          imageId = response.imageId
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to upload image')
          setUploadingImage(false)
          throw err
        }
        setUploadingImage(false)
      }

      // Create discussion on the user's profile
      return socialApi.createDiscussion({
        profileUserId: userId,
        title: title.trim(),
        content: content.trim(),
        permissionLevel: 0, // Public by default
        isPinned: false,
        imageId,
      })
    },
    onSuccess: () => {
      // Invalidate discussions query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['user-discussions', userId] })
      handleClose()
    },
    onError: (error: any) => {
      setError(error?.response?.data?.message || 'Failed to create blog post')
      setUploadingImage(false)
    },
  })

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    processImageFile(file)
  }

  const processImageFile = (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file')
      return
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB')
      return
    }

    setSelectedImage(file)

    // Create preview
    const reader = new FileReader()
    reader.onloadend = () => {
      setImagePreview(reader.result as string)
    }
    reader.readAsDataURL(file)
  }

  const handleRemoveImage = () => {
    setSelectedImage(null)
    setImagePreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handlePaste = (event: React.ClipboardEvent) => {
    const items = event.clipboardData?.items
    if (!items) return

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile()
        if (file) {
          event.preventDefault()
          processImageFile(file)
          break
        }
      }
    }
  }

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragOver(true)
  }

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragOver(false)
  }

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragOver(false)

    const files = event.dataTransfer?.files
    if (files && files.length > 0) {
      const file = files[0]
      if (file.type.startsWith('image/')) {
        processImageFile(file)
      }
    }
  }

  const handleClose = () => {
    setTitle('')
    setContent('')
    setSelectedImage(null)
    setImagePreview(null)
    setError(null)
    setUploadingImage(false)
    onClose()
  }

  const handleSubmit = () => {
    setError(null)

    // Validation
    if (!title.trim()) {
      setError('Title is required')
      return
    }
    if (!content.trim()) {
      setError('Content is required')
      return
    }
    if (title.trim().length < 3) {
      setError('Title must be at least 3 characters')
      return
    }
    if (content.trim().length < 10) {
      setError('Content must be at least 10 characters')
      return
    }

    createDiscussionMutation.mutate()
  }

  const isDisabled = createDiscussionMutation.isPending || uploadingImage

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '400px' }
      }}
    >
      <DialogTitle>Create New Blog Post</DialogTitle>
      <DialogContent
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          border: isDragOver ? '2px dashed #1976d2' : 'none',
          borderRadius: 1,
          backgroundColor: isDragOver ? '#e3f2fd' : 'transparent',
          transition: 'all 0.2s',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <TextField
            autoFocus
            label="Title"
            fullWidth
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter a catchy title for your post"
            disabled={isDisabled}
            inputProps={{ maxLength: 200 }}
            helperText={`${title.length}/200 characters`}
          />

          <TextField
            label="Content"
            fullWidth
            required
            multiline
            rows={8}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onPaste={handlePaste}
            placeholder={isDragOver ? "Drop image here..." : "Share your thoughts, experiences, or stories..."}
            disabled={isDisabled}
            inputProps={{ maxLength: 10000 }}
            helperText={`${content.length}/10000 characters`}
          />

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleImageSelect}
            style={{ display: 'none' }}
          />

          {/* Helper text - clickable */}
          <Box
            onClick={() => !selectedImage && !isDisabled && fileInputRef.current?.click()}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: isDisabled || selectedImage ? 'default' : 'pointer',
              opacity: isDisabled || selectedImage ? 0.5 : 1,
              '&:hover': {
                opacity: isDisabled || selectedImage ? 0.5 : 0.8,
              },
            }}
          >
            <CameraIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              Paste or drag images, or click the camera icon to upload
            </Typography>
          </Box>

          {/* Image Preview */}
          {imagePreview && (
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              <Box
                component="img"
                src={imagePreview}
                alt="Preview"
                sx={{
                  maxWidth: '100%',
                  maxHeight: 300,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              />
              <IconButton
                size="small"
                onClick={handleRemoveImage}
                disabled={isDisabled}
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  bgcolor: 'background.paper',
                  '&:hover': { bgcolor: 'background.paper' },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          {uploadingImage && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="caption" color="text.secondary">
                Uploading image...
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isDisabled}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isDisabled || !title.trim() || !content.trim()}
          startIcon={createDiscussionMutation.isPending ? <CircularProgress size={20} /> : null}
        >
          {uploadingImage ? 'Uploading...' : createDiscussionMutation.isPending ? 'Creating...' : 'Create Post'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
